import { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal.jsx';
import { Icons } from '../components/Icons.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useT } from '../lib/i18n.jsx';
import { useToast } from '../lib/toast.jsx';
import { useDialog } from '../lib/dialog.jsx';
import { errorMessage } from '../lib/errors.js';
import * as api from '../lib/api.js';
import { formatDate, initials, toDateInputValue, dateInputToPbDate } from '../lib/format.js';

function ageFromBirthdate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const years = (Date.now() - d.getTime()) / (365.25 * 86400000);
  return years >= 0 && years < 25 ? Math.floor(years) : null;
}

function AvatarImg({ avatar, name, size = 56 }) {
  const src = avatar?.file
    ? api.fileUrl(avatar.collection_id, avatar.id, avatar.file, '100x100')
    : null;

  if (!src) {
    return <span className="avatar-initials" style={{ width: size, height: size }}>{initials(name)}</span>;
  }
  return <img className="avatar-img" src={src} alt="" width={size} height={size} loading="lazy" />;
}

export default function Kids() {
  const t = useT();
  const toast = useToast();
  const dialog = useDialog();
  const { profiles, refresh } = useAuth();

  const [avatars, setAvatars] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | profile (edit)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api.getAvatars(controller.signal)
      .then((res) => setAvatars(res?.items ?? []))
      .catch((err) => { if (err?.name !== 'AbortError') setAvatars([]); });
    return () => controller.abort();
  }, []);

  const sorted = useMemo(
    () => [...profiles].sort((a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0)),
    [profiles],
  );

  async function remove(profile) {
    if (profile.is_main) {
      toast.info(t('kids.deleteMainBlocked'));
      return;
    }
    const ok = await dialog.confirm({
      title: t('kids.deleteConfirmLabel'),
      message: t('kids.deleteConfirm', { name: profile.name }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!ok) return;

    try {
      await api.deleteProfile(profile.id);
      await refresh();
      toast.success(t('kids.deleted'));
    } catch (err) {
      toast.error(errorMessage(err, t));
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>{t('kids.title')}</h1>
          <p className="muted">{t('kids.subtitle')}</p>
        </div>
        <button type="button" className="btn primary" onClick={() => setEditing({})}>
          <Icons.Plus size={18} />
          <span>{t('kids.add')}</span>
        </button>
      </header>

      {sorted.length === 0 ? (
        <section className="card empty-card">
          <p>{t('kids.empty')}</p>
        </section>
      ) : (
        <ul className="kid-grid">
          {sorted.map((p) => {
            const age = ageFromBirthdate(p.birthdate);
            return (
              <li key={p.id} className="kid-card">
                <AvatarImg avatar={p.avatar} name={p.name} />
                <div className="kid-meta">
                  <strong>
                    {p.name}
                    {p.is_main && <em className="tag">{t('kids.main')}</em>}
                  </strong>
                  <small>
                    {age != null ? t('kids.age', { years: age }) : formatDate(p.birthdate)}
                  </small>
                </div>
                <div className="kid-actions">
                  <button type="button" className="icon-btn" onClick={() => setEditing(p)} aria-label={t('common.edit')}>
                    <Icons.Pen size={16} />
                  </button>
                  {!p.is_main && (
                    <button type="button" className="icon-btn danger" onClick={() => remove(p)} aria-label={t('common.delete')}>
                      <Icons.X size={16} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <KidForm
          profile={editing.id ? editing : null}
          avatars={avatars}
          busy={busy}
          setBusy={setBusy}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            setEditing(null);
            await refresh();
            toast.success(message);
          }}
        />
      )}
    </>
  );
}

function KidForm({ profile, avatars, busy, setBusy, onClose, onSaved }) {
  const t = useT();
  const toast = useToast();
  const isNew = !profile;

  const [name, setName] = useState(profile?.name ?? '');
  const [birthdate, setBirthdate] = useState(toDateInputValue(profile?.birthdate));
  const [avatarId, setAvatarId] = useState(profile?.avatar?.id ?? '');
  const [error, setError] = useState(null);

  // A new profile must carry an avatar — the backend rejects a blank one with
  // invalid_avatar — so preselect the first of the pool once it loads.
  useEffect(() => {
    if (!avatarId && avatars.length > 0) setAvatarId(avatars[0].id);
  }, [avatars, avatarId]);

  async function submit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('kids.nameRequired'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await api.createProfile({
          name: trimmed,
          birthdate: dateInputToPbDate(birthdate),
          avatarId,
        });
        await onSaved(t('kids.created'));
      } else {
        await api.updateProfile({
          id: profile.id,
          name: trimmed,
          birthdate: dateInputToPbDate(birthdate),
          avatarId,
        });
        await onSaved(t('kids.updated'));
      }
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isNew ? t('kids.newTitle') : t('kids.editTitle')}
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="kid-form" className="btn primary" disabled={busy}>
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </>
      }
    >
      <form id="kid-form" onSubmit={submit} className="stack">
        <label className="field">
          <span>{t('kids.name')}</span>
          <input
            className={`input${error ? ' invalid' : ''}`}
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            autoFocus
            required
          />
          {error && <small className="field-error">{error}</small>}
        </label>

        <label className="field">
          <span>{t('kids.birthdate')}</span>
          <input
            className="input"
            type="date"
            dir="ltr"
            value={birthdate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setBirthdate(e.target.value)}
          />
        </label>

        <div className="field">
          <span>{t('kids.avatar')}</span>
          <ul className="avatar-picker">
            {avatars.map((a) => {
              const src = api.fileUrl(a.collection_id, a.id, a.file, '100x100');
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    className={`avatar-option${avatarId === a.id ? ' selected' : ''}`}
                    onClick={() => setAvatarId(a.id)}
                    aria-pressed={avatarId === a.id}
                    aria-label={a.name || 'avatar'}
                  >
                    {src ? <img src={src} alt="" width={48} height={48} loading="lazy" /> : a.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </form>
    </Modal>
  );
}
